import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { salesApi } from '@api/salesApi';
import type {
  CustomerFilters,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  SalesOrderFilters,
  SalesOrderLineDto,
  SalesOrderLineInput,
  CreateSalesOrderRequest,
  UpdateSalesOrderRequest,
} from '@/types/sales';

// ── Customers ─────────────────────────────────────────────

export function useCustomers(mdFilters: CustomerFilters) {
  return useQuery({
    queryKey: ['sales', 'customers', mdFilters],
    queryFn: () => salesApi.getCustomers(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useCustomerById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['sales', 'customers', mdId],
    queryFn: () => salesApi.getCustomerById(mdId!),
    enabled: !!mdId,
  });
}

export function useCustomerSearch(mdQuery: string) {
  return useQuery({
    queryKey: ['sales', 'customers', 'search', mdQuery],
    queryFn: () => salesApi.searchCustomers(mdQuery),
    enabled: mdQuery.length >= 2,
  });
}

export function useCreateCustomer() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateCustomerRequest) =>
      salesApi.createCustomer(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
      message.success('Клієнта створено');
    },
    onError: () => {
      message.error('Помилка при створенні клієнта');
    },
  });
}

export function useUpdateCustomer() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: UpdateCustomerRequest) =>
      salesApi.updateCustomer(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
      message.success('Клієнта оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні клієнта');
    },
  });
}

// ── Sales Orders ───────────────────────────────────────────

export function useSalesOrders(mdFilters: SalesOrderFilters) {
  return useQuery({
    queryKey: ['sales', 'orders', mdFilters],
    queryFn: () => salesApi.getSalesOrders(mdFilters),
    placeholderData: (mdPreviousData) => mdPreviousData,
  });
}

export function useSalesOrderById(mdId: string | undefined) {
  return useQuery({
    queryKey: ['sales', 'orders', mdId],
    queryFn: () => salesApi.getSalesOrderById(mdId!),
    enabled: !!mdId,
  });
}

export function useCreateSalesOrder() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdRequest: CreateSalesOrderRequest) =>
      salesApi.createSalesOrder(mdRequest),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      message.success('Замовлення створено');
    },
    onError: () => {
      message.error('Помилка при створенні замовлення');
    },
  });
}

export function useUpdateSalesOrder() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    // Шапка — PUT, рядки — окремими запитами (UpdateSalesOrderCommand рядків не приймає)
    mutationFn: async (mdParams: {
      request: UpdateSalesOrderRequest;
      originalLines: SalesOrderLineDto[];
      lines: SalesOrderLineInput[];
    }) => {
      await salesApi.updateSalesOrder(mdParams.request);
      await salesApi.syncSalesOrderLines(
        mdParams.request.id,
        mdParams.originalLines,
        mdParams.lines
      );
    },
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      message.success('Замовлення оновлено');
    },
    onError: () => {
      message.error('Помилка при оновленні замовлення');
    },
  });
}

export function useConfirmSO() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdId: string) => salesApi.confirmSalesOrder(mdId),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      message.success('Замовлення підтверджено');
    },
    onError: () => {
      message.error('Помилка при підтвердженні замовлення');
    },
  });
}

export function useCancelSO() {
  const mdQueryClient = useQueryClient();

  return useMutation({
    mutationFn: (mdParams: { id: string; reason: string }) =>
      salesApi.cancelSalesOrder(mdParams.id, mdParams.reason),
    onSuccess: () => {
      mdQueryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      message.success('Замовлення скасовано');
    },
    onError: () => {
      message.error('Помилка при скасуванні замовлення');
    },
  });
}
